import { pxArrayToRem, pxToRem, flexClassName } from '@archipro-design/aria';
import type { StyleRule } from '@archipro-design/aria';
import type { Position } from '@modules/product-tagging/type';
import type { Position as Direction } from '@fluentui/react-northstar/dist/es/utils/positioner/types';
import {
    TAG_SIZE,
    TAG_SIZE_NON_ADMIN,
} from '@modules/product-tagging/constant';
import { TAG_TOOLTIP_MAX_WIDTH } from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';

const TOOLTIP_ANIMATION_SPEED = 200;
const TOOLTIP_ANIMATION_DELAY = 100;
const Z_INDEX_ANIMATION_DELAY = TOOLTIP_ANIMATION_SPEED / 2; //needed to smooth out dot reveal under a tooltip

export const TagContainer: StyleRule = () => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: '100%',
    width: '100%',
});

const animateTooltip = (
    tooltipVisible: boolean,
    tooltipDirection: Direction,
    reversedScale = false,
    reversedDirection = false
) => {
    return {
        transform: tooltipVisible
            ? reversedScale
                ? 'scale(0)'
                : 'scale(1)'
            : reversedScale
            ? 'scale(1)'
            : 'scale(0)',
        transformOrigin:
            tooltipDirection === 'above'
                ? reversedDirection
                    ? '50% 0'
                    : '50% 100%'
                : tooltipDirection === 'after'
                ? reversedDirection
                    ? '100% 50%'
                    : '0 50%'
                : tooltipDirection === 'below'
                ? reversedDirection
                    ? '50% 100%'
                    : '50% 0'
                : reversedDirection
                ? '0 50%'
                : '100% 50%',
        transition: `all ${TOOLTIP_ANIMATION_SPEED}ms ease ${TOOLTIP_ANIMATION_DELAY}ms`,
    };
};

export const TagTooltip: StyleRule<{
    tooltipVisible: boolean;
    tooltipDirection: Direction;
    forceReveal: boolean;
}> = ({ theme, tooltipVisible, tooltipDirection, forceReveal }) => ({
    //overwrite popper styles in order to do animation, no cleaner way found to do this
    padding: '0 !important',
    backgroundColor: 'transparent !important',
    zIndex: tooltipVisible
        ? theme.siteVariables.zIndexes.menuItem
        : theme.siteVariables.zIndexes.background,
    transition: `z-index ${Z_INDEX_ANIMATION_DELAY}ms linear ${Z_INDEX_ANIMATION_DELAY}ms`,
    '&:hover': {
        zIndex:
            forceReveal || tooltipVisible
                ? theme.siteVariables.zIndexes.menuItem
                : theme.siteVariables.zIndexes.background,
    },
    '& > div:first-of-type': {
        ':before': {
            ...animateTooltip(tooltipVisible, tooltipDirection, false, true),
        },
        ':after': {
            content: 'none', //we don't need to show double pointers.
        },
    },
});

export const TagTooltipBody: StyleRule<{
    tooltipVisible: boolean;
    tooltipDirection: Direction;
}> = ({ theme, tooltipVisible, tooltipDirection }) => ({
    ...theme.siteVariables.textStyles.Text.Medium65,
    fontSize: pxToRem(18),
    letterSpacing: pxToRem(-0.18),
    ...animateTooltip(tooltipVisible, tooltipDirection),
    padding: pxToRem(18),
    backgroundColor: theme.siteVariables.colors.charcoal['250'],
    borderRadius: pxToRem(2),
    maxWidth: pxToRem(TAG_TOOLTIP_MAX_WIDTH),
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [`& .${flexClassName}`]: {
        marginTop: pxToRem(5),
    },
});

export const ProfessionalTitle: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.textStyles.Text.Roman55,
    fontSize: pxToRem(16),
    letterSpacing: pxToRem(-0.32),
    lineHeight: theme.siteVariables.lineHeightSmallest,
    maxWidth: pxToRem(235),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

export const ProductPrice: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.textStyles.Text.Roman55,
    fontSize: pxToRem(16),
    lineHeight: theme.siteVariables.lineHeightSmallest,
    letterSpacing: pxToRem(-0.32),
    color: theme.siteVariables.colors.grey['300'],
    marginLeft: pxToRem(11),
});

export const TagIndicatorHoverArea: StyleRule<{
    tagPosition: Position;
    tooltipDirection: Direction;
}> = ({ theme, tagPosition, tooltipDirection }) => ({
    position: 'absolute',
    top: `${tagPosition.y}%`,
    left: `${tagPosition.x}%`,
    height: pxToRem(TAG_SIZE),
    width: pxToRem(TAG_SIZE),
    cursor: 'pointer',
    zIndex: theme.siteVariables.zIndexes.foreground,
    //pixel perfect adjustment, popper calculations is 1 pixel off center.
    padding:
        tooltipDirection === 'above' || tooltipDirection === 'below'
            ? pxArrayToRem([14, 13, 14, 15])
            : pxArrayToRem([15, 14, 13, 14]),
});

export const TagIndicator: StyleRule<{
    tooltipVisible: boolean;
    dotVisible: boolean;
    tooltipDirection: Direction;
}> = ({ theme, tooltipVisible, dotVisible, tooltipDirection }) => ({
    borderRadius: '50%',
    backgroundColor: theme.siteVariables.colors.primitive.white,
    boxShadow: `0px 1px 5px rgba(0, 0, 0, 0.30)`,
    height: pxToRem(TAG_SIZE_NON_ADMIN),
    width: pxToRem(TAG_SIZE_NON_ADMIN),
    opacity: dotVisible && !tooltipVisible ? 1 : 0,
    ...animateTooltip(tooltipVisible, tooltipDirection, true, true),
});

export const TagsToggle: StyleRule<{
    showToggle: boolean;
}> = ({ theme, showToggle }) => ({
    position: 'absolute',
    left: pxToRem(12),
    bottom: pxToRem(12),
    opacity: showToggle ? 1 : 0,
    transition: `all ${TOOLTIP_ANIMATION_SPEED}ms ease`,
    boxShadow: `0px 1px 5px rgba(0, 0, 0, 0.30)`,
    borderRadius: pxToRem(19),
    backgroundColor: theme.siteVariables.colors.primitive.white,
    cursor: 'pointer',
    [`&:hover > div`]: {
        maxWidth: pxToRem(500),
        opacity: 1,
        padding: pxArrayToRem([9, 12, 9, 0]),
    },
});

export const TagsToggleText: StyleRule<{ forceReveal: boolean }> = ({
    theme,
    forceReveal,
}) => ({
    ...theme.siteVariables.textStyles.Text.Roman55,
    fontSize: pxToRem(14),
    color: theme.siteVariables.colors.primitive.black,
    padding: forceReveal ? pxArrayToRem([9, 12, 9, 6]) : pxArrayToRem([9, 0]),
    maxWidth: forceReveal ? pxToRem(500) : 0,
    opacity: forceReveal ? 1 : 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: `all ${TOOLTIP_ANIMATION_SPEED}ms ease`,
});
